import React, {Fragment, useState} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';
import movieNightImage from './movie-night.svg';
import {DataTableAddItemButton} from '@common/datatable/data-table-add-item-button';
import {Link} from 'react-router-dom';
import {Lien} from '@app/titles/models/lien';
import {ColumnConfig} from '@common/datatable/column-config';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {FormattedNumber} from '@ui/i18n/formatted-number';
import {EditIcon} from '@ui/icons/material/Edit';

import {EnumLinkQualityChip} from '@ui/i18n/enum-quality';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {ValidationSelectedItemsAction} from '@common/datatable/page/validation-links-selected-items-action';
import {TitlePoster} from '@app/titles/title-poster/title-poster';
import {TitleLink} from '@app/titles/title-link';

export function LiensListDatatablePageAdminValidation() {
  const actions: ColumnConfig<Lien>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      width: 'w-84 flex-shrink-0',
      body: lien => (
        <Fragment>
          <Link
            to={`/admin/liens/${lien.title_id}/${lien.id}/edit`}
            className="text-muted"
          >
            <Tooltip label={<Trans message="Edit" />}>
              <IconButton size="md">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Link>
        </Fragment>
      ),
    },
  ];
  const commonColumns: ColumnConfig<Lien>[] = [
    {
      key: 'id',
      width: 'flex-3',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="ID" />,
      body: lien => lien.id,
    },
    {
      key: 'title',
      allowsSorting: true,
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Title" />,
      body: lien =>
        lien.post ? (
          <div className="flex items-center gap-12">
            <TitlePoster
              title={lien.post}
              srcSize="sm"
              size="w-32"
              showLastLink={false}
              aspect="aspect-square"
            />
            <div className="min-w-0 overflow-hidden">
              <div className="overflow-hidden overflow-ellipsis">
                <TitleLink title={lien.post} target="_blank" />
              </div>
            </div>
          </div>
        ) : null,
    },
    {
      key: 'Uploader',
      visibleInMode: 'regular',
      allowsSorting: false,
      header: () => <Trans message="Uploader" />,
      body: lien => (
        <Link target={'_blank'} to={`/get-user/${lien.id_user}`}>
          {lien.id_user}
        </Link>
      ),
    },
    {
      key: 'lien',
      width: 'flex-3',
      visibleInMode: 'all',
      header: () => <Trans message="Lien" />,
      body: lien => (
        <Fragment>
          <a href={lien.lien} target="_blank" rel="noreferrer">
            {lien.lien}
          </a>
        </Fragment>
      ),
    },
    {
      key: 'categorie',
      allowsSorting: true,
      header: () => <Trans message="Category" />,
      body: lien => lien.post?.categorie?.name,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'qualite',
      allowsSorting: true,
      header: () => <Trans message="Quality" />,
      body: lien => <EnumLinkQualityChip quality={lien.qual?.qual} />,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'langues_compact',
      header: () => <Trans message="Languages" />,
      body: lien => (
        <ChipList radius="rounded" size="xs">
          {lien?.langues_compact?.map(lang => (
            <Chip key={lang.id} selectable>
              <Trans message={lang.name} />
            </Chip>
          ))}
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'subs_compact',
      header: () => <Trans message="Subtitles" />,
      body: lien => (
        <ChipList radius="rounded" size="xs">
          {lien?.subs_compact?.map(lang => (
            <Chip key={lang.id} selectable>
              <Trans message={lang.name} />
            </Chip>
          ))}
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'saison',
      allowsSorting: true,
      header: () => <Trans message="Season" />,
      body: lien => <FormattedNumber value={lien.saison} />,
    },
    {
      key: 'episode',
      allowsSorting: true,
      header: () => <Trans message="Episode" />,
      body: lien => <FormattedNumber value={lien.episode} />,
    },
  ];

  const LiensListDatatableColumnsSerie: ColumnConfig<Lien>[] = [
    ...commonColumns,
    ...actions,
  ];

  return (
    <DataTablePage
      endpoint={'liens'}
      queryParams={{loader: 'admin-links-validation'}}
      title={<Trans message="Links validations" />}
      columns={LiensListDatatableColumnsSerie}
      selectedActions={<ValidationSelectedItemsAction />}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={movieNightImage}
          title={<Trans message="No links have been created yet" />}
          filteringTitle={<Trans message="No matching links" />}
        />
      }
    />
  );
}

function Actions() {
  return (
    <Fragment>
      <DataTableAddItemButton elementType={Link} to="new">
        <Trans message="Add Links" />
      </DataTableAddItemButton>
    </Fragment>
  );
}
